/**
 * PdfExtractor — Extracts pages from a PDF as images using pdf.js
 *
 * Uses pdfjs-dist v4 with a Vite-compatible web worker for offloading
 * PDF parsing. Pages are rendered lazily — only when explicitly requested.
 *
 * @module PdfExtractor
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Configure the pdf.js worker source for Vite
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * @typedef {Object} PageResult
 * @property {number}            pageNumber  1-based page index
 * @property {HTMLCanvasElement}  canvas      Canvas with the rendered page
 * @property {number}            width       Rendered width in px
 * @property {number}            height      Rendered height in px
 * @property {string}            dataUrl     PNG data-URL of the rendered page
 */

/**
 * Extracts pages from a PDF file as rasterised images.
 *
 * Usage:
 * ```js
 * const extractor = new PdfExtractor();
 * const count = await extractor.loadPdf(file);
 * const page1 = await extractor.extractPage(1);
 * extractor.destroy();
 * ```
 */
export class PdfExtractor {
  /** @type {import('pdfjs-dist').PDFDocumentProxy | null} */
  #pdfDocument = null;

  /** @type {number} */
  #pageCount = 0;

  /** @type {Map<string, PageResult>} cache keyed by `${pageNumber}@${scale}` */
  #cache = new Map();

  /**
   * Load a PDF from a File (or Blob).
   *
   * @param {File | Blob} file  The PDF file to load
   * @returns {Promise<number>} Total number of pages in the document
   * @throws {Error} If the file cannot be read or is not a valid PDF
   */
  async loadPdf(file) {
    // Clean up any previously loaded document
    this.destroy();

    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        // Disable auto-fetching since we already have the full buffer
        disableAutoFetch: true,
        // Use fewer streams — we render on demand
        disableStream: true,
      });

      this.#pdfDocument = await loadingTask.promise;
      this.#pageCount = this.#pdfDocument.numPages;

      return this.#pageCount;
    } catch (err) {
      this.#pdfDocument = null;
      this.#pageCount = 0;
      throw new Error(`Failed to load PDF: ${err.message}`);
    }
  }

  /**
   * Render a single page to an offscreen canvas.
   *
   * Results are cached — calling again with the same page number and scale
   * will return the previously rendered result without re-rasterising.
   *
   * @param {number} pageNumber  1-based page index
   * @param {number} [scale=2.0] Render scale (2 = 144 DPI for a 72-DPI page)
   * @returns {Promise<PageResult>}
   * @throws {Error} If no PDF is loaded or the page number is out of range
   */
  async extractPage(pageNumber, scale = 2.0) {
    this.#assertLoaded();

    if (pageNumber < 1 || pageNumber > this.#pageCount) {
      throw new Error(
        `Page ${pageNumber} is out of range (1–${this.#pageCount})`
      );
    }

    // Return from cache if already rendered at this scale
    const cacheKey = `${pageNumber}@${scale}`;
    if (this.#cache.has(cacheKey)) {
      return /** @type {PageResult} */ (this.#cache.get(cacheKey));
    }

    try {
      const page = await this.#pdfDocument.getPage(pageNumber);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);

      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;

      const result = /** @type {PageResult} */ ({
        pageNumber,
        canvas,
        width: canvas.width,
        height: canvas.height,
        // Lazy dataUrl — only generated on first access to avoid
        // multi-MB PNG string creation for every page at high scales
        get dataUrl() {
          if (!this._dataUrl) {
            this._dataUrl = canvas.toDataURL('image/png');
          }
          return this._dataUrl;
        },
      });

      this.#cache.set(cacheKey, result);
      return result;
    } catch (err) {
      throw new Error(
        `Failed to render page ${pageNumber}: ${err.message}`
      );
    }
  }

  /**
   * Extract every page in the document.
   *
   * @param {number} [scale=2.0]                        Render scale
   * @param {(current: number, total: number) => void} [onProgress] Called after each page
   * @returns {Promise<PageResult[]>} Ordered array of page results
   */
  async extractAllPages(scale = 2.0, onProgress) {
    this.#assertLoaded();

    const results = [];

    for (let i = 1; i <= this.#pageCount; i++) {
      const page = await this.extractPage(i, scale);
      results.push(page);

      if (typeof onProgress === 'function') {
        onProgress(i, this.#pageCount);
      }
    }

    return results;
  }

  /**
   * @returns {number} Total page count (0 if no PDF loaded)
   */
  getPageCount() {
    return this.#pageCount;
  }

  /**
   * Release the underlying pdf.js document and clear the render cache.
   */
  destroy() {
    if (this.#pdfDocument) {
      this.#pdfDocument.destroy();
      this.#pdfDocument = null;
    }
    this.#pageCount = 0;
    this.#cache.clear();
  }

  // ── Private helpers ─────────────────────────────────────────────────

  /**
   * Guard that throws if no PDF is currently loaded.
   * @private
   */
  #assertLoaded() {
    if (!this.#pdfDocument) {
      throw new Error('No PDF loaded — call loadPdf() first');
    }
  }
}
